import type { LucideIcon } from "lucide-react";
import {
  ShieldCheck,
  Code2,
  ShieldAlert,
  Shield,
  Server,
  ScanSearch,
  BrickWall,
  Network,
} from "lucide-react";

export type Lang = "nl" | "en" | "fr";
export const LANGS: Lang[] = ["nl", "en", "fr"];

type L<T> = Record<Lang, T>;

export interface ServiceDef {
  key: string;
  icon: LucideIcon;
  /** Shows the "PHP hack" alert block */
  urgent?: boolean;
  slug: L<string>;
  name: L<string>;
  /** Heading used with a place, e.g. "Website beveiligen in {place}" */
  placeTitle: L<string>;
  short: L<string>;
  intro: L<string>;
  bullets: L<string[]>;
  faq: L<{ q: string; a: string }[]>;
  related: string[];
}

export const SERVICES: ServiceDef[] = [
  {
    key: "website-beveiliging",
    icon: ShieldCheck,
    urgent: true,
    slug: { nl: "website-beveiliging", en: "website-security", fr: "securite-site-web" },
    name: { nl: "Website update & beveiliging", en: "Website updates & security", fr: "Mise à jour & sécurité de site web" },
    placeTitle: { nl: "Website updaten en beveiligen in {place}", en: "Website updates and security in {place}", fr: "Mise à jour et sécurité de site web à {place}" },
    short: {
      nl: "Verouderde PHP, WordPress of CMS bijwerken en lekken dichten voordat hackers ze vinden.",
      en: "Update outdated PHP, WordPress or CMS installs and close leaks before attackers find them.",
      fr: "Mettre à jour PHP, WordPress ou votre CMS et fermer les failles avant les pirates.",
    },
    intro: {
      nl: "Draait je website in {place} nog op een oude PHP-versie of een verouderd CMS? Dat is precies waar de huidige PHP-aanvallen op mikken. Wij controleren je site, werken PHP, plugins en thema's veilig bij en sluiten de bekende lekken.",
      en: "Is your website in {place} still running an old PHP version or an outdated CMS? That is exactly what the current PHP attacks target. We check your site, safely update PHP, plugins and themes, and close known vulnerabilities.",
      fr: "Votre site à {place} tourne encore sur une ancienne version de PHP ou un CMS obsolète ? C'est exactement la cible des attaques PHP actuelles. Nous vérifions votre site, mettons PHP, extensions et thèmes à jour et fermons les failles connues.",
    },
    bullets: {
      nl: ["PHP-versie veilig upgraden zonder dat je site breekt", "WordPress, Joomla, Drupal en maatwerk-CMS bijwerken", "Kwetsbare plugins en thema's opsporen en vervangen", "Beveiligingsheaders, SSL en sterke wachtwoorden instellen", "Doorlopend onderhoud en automatische back-ups"],
      en: ["Upgrade PHP safely without breaking your site", "Update WordPress, Joomla, Drupal and custom CMS", "Find and replace vulnerable plugins and themes", "Set up security headers, SSL and strong passwords", "Ongoing maintenance and automatic backups"],
      fr: ["Mise à niveau sûre de PHP sans casser votre site", "Mise à jour de WordPress, Joomla, Drupal et CMS sur mesure", "Détection et remplacement des extensions vulnérables", "En-têtes de sécurité, SSL et mots de passe forts", "Maintenance continue et sauvegardes automatiques"],
    },
    faq: {
      nl: [
        { q: "Is mijn website kwetsbaar voor de PHP-hack?", a: "Websites op een PHP-versie zonder beveiligingsupdates of met verouderde plugins lopen het grootste risico. Wij controleren dit snel voor je." },
        { q: "Gaat mijn website kapot door een PHP-update?", a: "We testen de update eerst op een kopie van je site. Pas als alles werkt, zetten we hem live." },
        { q: "Hoe snel kunnen jullie helpen?", a: "Bij acute dreiging kijken we meestal dezelfde werkdag mee. Bel ons direct voor spoed." },
      ],
      en: [
        { q: "Is my website vulnerable to the PHP hack?", a: "Sites on a PHP version without security updates or with outdated plugins are most at risk. We can check this quickly for you." },
        { q: "Will a PHP update break my website?", a: "We test the update on a copy of your site first and only go live once everything works." },
        { q: "How fast can you help?", a: "For acute threats we usually look at it the same working day. Call us directly for urgent cases." },
      ],
      fr: [
        { q: "Mon site est-il vulnérable au piratage PHP ?", a: "Les sites sur une version de PHP sans mises à jour de sécurité ou avec des extensions obsolètes sont les plus exposés. Nous le vérifions rapidement." },
        { q: "Une mise à jour PHP va-t-elle casser mon site ?", a: "Nous testons d'abord la mise à jour sur une copie de votre site, puis la mettons en ligne quand tout fonctionne." },
        { q: "En combien de temps pouvez-vous aider ?", a: "En cas de menace aiguë, nous intervenons généralement le jour ouvrable même. Appelez-nous pour les urgences." },
      ],
    },
    related: ["website-gehackt", "website-laten-bouwen", "cyber-scan"],
  },
  {
    key: "website-laten-bouwen",
    icon: Code2,
    slug: { nl: "website-laten-bouwen", en: "website-development", fr: "creation-site-web" },
    name: { nl: "Nieuwe website laten bouwen", en: "New website development", fr: "Création de nouveau site web" },
    placeTitle: { nl: "Veilige website laten bouwen in {place}", en: "Secure website development in {place}", fr: "Création de site web sécurisé à {place}" },
    short: {
      nl: "Is je oude site niet meer te redden? Wij bouwen een snelle, veilige nieuwe website.",
      en: "Old site beyond repair? We build a fast, secure new website.",
      fr: "Ancien site irrécupérable ? Nous créons un nouveau site rapide et sécurisé.",
    },
    intro: {
      nl: "Soms is bijwerken duurder dan opnieuw beginnen. Voor ondernemers in {place} bouwen we een nieuwe website op een moderne, veilige basis — zonder verouderde PHP, met automatische updates en hosting die we zelf bewaken.",
      en: "Sometimes updating costs more than starting over. For businesses in {place} we build a new website on a modern, secure foundation — no outdated PHP, with automatic updates and hosting we monitor ourselves.",
      fr: "Parfois, mettre à jour coûte plus cher que repartir de zéro. Pour les entreprises de {place}, nous créons un nouveau site sur une base moderne et sûre — sans PHP obsolète, avec mises à jour automatiques et un hébergement que nous surveillons.",
    },
    bullets: {
      nl: ["Moderne techniek zonder bekende PHP-lekken", "Je bestaande teksten en vindbaarheid in Google blijven behouden", "Snel op mobiel en desktop", "Beveiligde hosting met monitoring", "Zelf je teksten aanpassen"],
      en: ["Modern technology without known PHP vulnerabilities", "Keep your existing content and Google rankings", "Fast on mobile and desktop", "Secure hosting with monitoring", "Edit your own content"],
      fr: ["Technologie moderne sans failles PHP connues", "Conservation de vos contenus et de votre référencement", "Rapide sur mobile et ordinateur", "Hébergement sécurisé et surveillé", "Modifiez vous-même vos textes"],
    },
    faq: {
      nl: [
        { q: "Wanneer is een nieuwe website beter dan updaten?", a: "Als je site op een zeer oude PHP-versie draait, maatwerk bevat dat niet meer onderhouden wordt of al eens gehackt is, is nieuwbouw vaak goedkoper en veiliger." },
        { q: "Verlies ik mijn Google-posities?", a: "Nee. We nemen je bestaande adressen en teksten mee en zetten doorverwijzingen goed." },
        { q: "Hoe lang duurt het bouwen?", a: "Een gemiddelde bedrijfswebsite staat binnen enkele weken live." },
      ],
      en: [
        { q: "When is a new website better than updating?", a: "If your site runs a very old PHP version, contains unmaintained custom code or has been hacked before, rebuilding is often cheaper and safer." },
        { q: "Will I lose my Google rankings?", a: "No. We keep your existing URLs and content and set up proper redirects." },
        { q: "How long does it take?", a: "An average business website is live within a few weeks." },
      ],
      fr: [
        { q: "Quand un nouveau site vaut-il mieux qu'une mise à jour ?", a: "Si votre site tourne sur une très vieille version de PHP, contient du code non maintenu ou a déjà été piraté, la refonte est souvent moins chère et plus sûre." },
        { q: "Vais-je perdre mon référencement Google ?", a: "Non. Nous conservons vos adresses et contenus et mettons en place les redirections." },
        { q: "Combien de temps faut-il ?", a: "Un site d'entreprise moyen est en ligne en quelques semaines." },
      ],
    },
    related: ["website-beveiliging", "website-gehackt", "cyber-scan"],
  },
  {
    key: "website-gehackt",
    icon: ShieldAlert,
    urgent: true,
    slug: { nl: "website-gehackt", en: "website-hacked", fr: "site-pirate" },
    name: { nl: "Website gehackt? Herstel", en: "Hacked website recovery", fr: "Site piraté : restauration" },
    placeTitle: { nl: "Website gehackt in {place}? Direct herstel", en: "Website hacked in {place}? Fast recovery", fr: "Site piraté à {place} ? Restauration rapide" },
    short: {
      nl: "Vreemde pagina's, spam of een waarschuwing in Google? Wij schonen je site op en dichten het lek.",
      en: "Strange pages, spam or a Google warning? We clean your site and close the hole.",
      fr: "Pages étranges, spam ou alerte Google ? Nous nettoyons votre site et fermons la faille.",
    },
    intro: {
      nl: "Is je website in {place} gehackt, bijvoorbeeld via het recente PHP-lek? Wij verwijderen kwaadaardige code, herstellen je site, zoeken uit hoe de aanvaller binnenkwam en zorgen dat het niet opnieuw gebeurt.",
      en: "Has your website in {place} been hacked, for example through the recent PHP vulnerability? We remove malicious code, restore your site, find out how the attacker got in and make sure it doesn't happen again.",
      fr: "Votre site à {place} a été piraté, par exemple via la récente faille PHP ? Nous supprimons le code malveillant, restaurons votre site, identifions le point d'entrée et empêchons une récidive.",
    },
    bullets: {
      nl: ["Malware, backdoors en spam-pagina's verwijderen", "Oorzaak van de hack vinden en dichten", "Google-waarschuwingen en zwarte lijsten laten opheffen", "Wachtwoorden en toegang opnieuw veilig instellen", "Rapport van wat er is gebeurd"],
      en: ["Remove malware, backdoors and spam pages", "Find and close the cause of the hack", "Get Google warnings and blacklists lifted", "Reset passwords and access securely", "Report of what happened"],
      fr: ["Suppression des malwares, portes dérobées et pages de spam", "Identification et fermeture de la cause", "Levée des alertes Google et listes noires", "Réinitialisation sécurisée des accès", "Rapport de l'incident"],
    },
    faq: {
      nl: [
        { q: "Hoe weet ik of mijn website gehackt is?", a: "Signalen zijn onbekende pagina's, doorverwijzingen naar vreemde sites, spam in Google of een waarschuwing van je hostingpartij." },
        { q: "Moet ik mijn website offline halen?", a: "Bel ons eerst. Vaak is het beter de site tijdelijk af te schermen zodat sporen bewaard blijven." },
        { q: "Kunnen jullie ook in het weekend helpen?", a: "Voor spoedgevallen zijn we telefonisch bereikbaar." },
      ],
      en: [
        { q: "How do I know my website was hacked?", a: "Signs include unknown pages, redirects to strange sites, spam in Google or a warning from your host." },
        { q: "Should I take my website offline?", a: "Call us first. It's often better to shield the site temporarily so evidence is preserved." },
        { q: "Can you help at weekends?", a: "For emergencies we can be reached by phone." },
      ],
      fr: [
        { q: "Comment savoir si mon site a été piraté ?", a: "Pages inconnues, redirections suspectes, spam dans Google ou alerte de votre hébergeur sont des signes." },
        { q: "Dois-je mettre mon site hors ligne ?", a: "Appelez-nous d'abord. Il vaut souvent mieux isoler le site temporairement pour conserver les traces." },
        { q: "Intervenez-vous le week-end ?", a: "Pour les urgences, nous sommes joignables par téléphone." },
      ],
    },
    related: ["website-beveiliging", "website-laten-bouwen", "cybersecurity"],
  },
  {
    key: "cybersecurity",
    icon: Shield,
    slug: { nl: "cybersecurity", en: "cybersecurity", fr: "cybersecurite" },
    name: { nl: "Cybersecurity hulp", en: "Cybersecurity support", fr: "Aide en cybersécurité" },
    placeTitle: { nl: "Cybersecurity hulp in {place}", en: "Cybersecurity support in {place}", fr: "Aide en cybersécurité à {place}" },
    short: {
      nl: "Advies, bescherming en hulp bij incidenten voor mkb en organisaties.",
      en: "Advice, protection and incident response for SMEs and organisations.",
      fr: "Conseil, protection et réponse aux incidents pour PME et organisations.",
    },
    intro: {
      nl: "Ransomware, phishing of een datalek: voor bedrijven in {place} is cybersecurity geen luxe meer. Wij helpen je risico's in kaart te brengen, je systemen te beschermen en snel te handelen als het misgaat.",
      en: "Ransomware, phishing or a data breach: for businesses in {place} cybersecurity is no longer optional. We help you map risks, protect your systems and act fast when things go wrong.",
      fr: "Rançongiciel, hameçonnage ou fuite de données : pour les entreprises de {place}, la cybersécurité n'est plus un luxe. Nous cartographions vos risques, protégeons vos systèmes et agissons vite en cas d'incident.",
    },
    bullets: {
      nl: ["Hulp bij ransomware en andere incidenten", "Beveiligingsadvies op maat voor het mkb", "Bescherming van e-mail, laptops en servers", "Hulp bij NIS2 en AVG-eisen", "Bewustwordingstraining voor medewerkers"],
      en: ["Help with ransomware and other incidents", "Tailored security advice for SMEs", "Protection for email, laptops and servers", "Support with NIS2 and GDPR requirements", "Security awareness training for staff"],
      fr: ["Aide face aux rançongiciels et autres incidents", "Conseil en sécurité adapté aux PME", "Protection de la messagerie, des postes et serveurs", "Accompagnement NIS2 et RGPD", "Sensibilisation du personnel"],
    },
    faq: {
      nl: [
        { q: "Wat moet ik doen bij een ransomware-aanval?", a: "Koppel getroffen apparaten los van het netwerk, betaal niet en bel ons direct." },
        { q: "Is mijn bedrijf te klein voor cybersecurity?", a: "Nee. Juist kleinere bedrijven zijn vaak doelwit omdat ze minder beschermd zijn." },
        { q: "Helpen jullie met NIS2?", a: "Ja, we brengen in kaart wat er voor jouw organisatie nodig is en helpen bij de uitvoering." },
      ],
      en: [
        { q: "What should I do during a ransomware attack?", a: "Disconnect affected devices from the network, don't pay and call us right away." },
        { q: "Is my company too small for cybersecurity?", a: "No. Smaller companies are often targeted precisely because they are less protected." },
        { q: "Do you help with NIS2?", a: "Yes, we map what your organisation needs and help implement it." },
      ],
      fr: [
        { q: "Que faire en cas d'attaque par rançongiciel ?", a: "Déconnectez les appareils touchés du réseau, ne payez pas et appelez-nous immédiatement." },
        { q: "Mon entreprise est-elle trop petite ?", a: "Non. Les petites entreprises sont souvent ciblées car moins protégées." },
        { q: "Aidez-vous pour NIS2 ?", a: "Oui, nous identifions vos besoins et vous accompagnons dans la mise en œuvre." },
      ],
    },
    related: ["cyber-scan", "firewall", "security-hardware-software"],
  },
  {
    key: "security-hardware-software",
    icon: Server,
    slug: { nl: "security-hardware-software", en: "security-hardware-software", fr: "materiel-logiciel-securite" },
    name: { nl: "Security hardware & software", en: "Security hardware & software", fr: "Matériel & logiciels de sécurité" },
    placeTitle: { nl: "Security hardware en software leveren in {place}", en: "Security hardware and software supply in {place}", fr: "Fourniture de matériel et logiciels de sécurité à {place}" },
    short: {
      nl: "Levering en installatie van firewalls, endpoint-beveiliging, back-up en meer.",
      en: "Supply and installation of firewalls, endpoint protection, backup and more.",
      fr: "Fourniture et installation de pare-feu, protection des postes, sauvegarde et plus.",
    },
    intro: {
      nl: "Wij leveren beveiligingsapparatuur en -software aan bedrijven in {place} en installeren het ook: van firewall en antivirus tot versleutelde back-up en veilige werkplekken. Je krijgt één aanspreekpunt voor levering, installatie en beheer.",
      en: "We supply and install security hardware and software for businesses in {place}: from firewalls and antivirus to encrypted backup and secure workplaces. One point of contact for delivery, installation and management.",
      fr: "Nous fournissons et installons du matériel et des logiciels de sécurité pour les entreprises de {place} : pare-feu, antivirus, sauvegarde chiffrée et postes sécurisés. Un seul interlocuteur pour la livraison, l'installation et la gestion.",
    },
    bullets: {
      nl: ["Firewalls van onder meer Stormshield", "Endpoint-beveiliging (antivirus/EDR) voor alle apparaten", "Versleutelde back-up en herstel", "Licenties beheren en verlengen", "Installatie op locatie of op afstand"],
      en: ["Firewalls including Stormshield", "Endpoint protection (antivirus/EDR) for all devices", "Encrypted backup and recovery", "License management and renewals", "On-site or remote installation"],
      fr: ["Pare-feu, notamment Stormshield", "Protection des postes (antivirus/EDR)", "Sauvegarde chiffrée et restauration", "Gestion et renouvellement des licences", "Installation sur site ou à distance"],
    },
    faq: {
      nl: [
        { q: "Welke merken leveren jullie?", a: "We werken onder andere met Stormshield en kiezen per situatie de best passende oplossing." },
        { q: "Installeren jullie ook?", a: "Ja, we leveren, installeren en kunnen het beheer overnemen." },
        { q: "Leveren jullie ook aan partners?", a: "Ja, IT-partners kunnen via ons inkopen. Neem contact op voor de voorwaarden." },
      ],
      en: [
        { q: "Which brands do you supply?", a: "We work with Stormshield among others and choose the best fit for each situation." },
        { q: "Do you install as well?", a: "Yes, we supply, install and can take over management." },
        { q: "Do you supply resellers?", a: "Yes, IT partners can purchase through us. Contact us for terms." },
      ],
      fr: [
        { q: "Quelles marques fournissez-vous ?", a: "Nous travaillons entre autres avec Stormshield et choisissons la solution la plus adaptée." },
        { q: "Assurez-vous l'installation ?", a: "Oui, nous livrons, installons et pouvons assurer la gestion." },
        { q: "Fournissez-vous des revendeurs ?", a: "Oui, les partenaires IT peuvent acheter chez nous. Contactez-nous pour les conditions." },
      ],
    },
    related: ["firewall", "bedrijfsnetwerk", "cybersecurity"],
  },
  {
    key: "cyber-scan",
    icon: ScanSearch,
    slug: { nl: "cyber-scan", en: "cyber-scan", fr: "scan-cyber" },
    name: { nl: "Cyber scan", en: "Cyber scan", fr: "Scan de cybersécurité" },
    placeTitle: { nl: "Cyber scan voor bedrijven in {place}", en: "Cyber scan for businesses in {place}", fr: "Scan de cybersécurité pour entreprises à {place}" },
    short: {
      nl: "We zoeken de zwakke plekken in je website en netwerk voordat een aanvaller dat doet.",
      en: "We find the weak spots in your website and network before an attacker does.",
      fr: "Nous trouvons les points faibles de votre site et réseau avant un attaquant.",
    },
    intro: {
      nl: "Weet je hoe kwetsbaar je organisatie in {place} is? Met een cyber scan controleren we je website, servers en netwerk op bekende lekken — waaronder verouderde PHP — en krijg je een helder rapport met concrete verbeterpunten.",
      en: "Do you know how vulnerable your organisation in {place} is? A cyber scan checks your website, servers and network for known vulnerabilities — including outdated PHP — and gives you a clear report with concrete improvements.",
      fr: "Savez-vous à quel point votre organisation à {place} est vulnérable ? Un scan vérifie votre site, vos serveurs et votre réseau à la recherche de failles connues — dont PHP obsolète — avec un rapport clair et des actions concrètes.",
    },
    bullets: {
      nl: ["Website- en PHP-kwetsbaarheidsscan", "Scan van open poorten en externe systemen", "Controle op gelekte wachtwoorden", "Begrijpelijk rapport met prioriteiten", "Herhaalscan na het oplossen"],
      en: ["Website and PHP vulnerability scan", "Scan of open ports and external systems", "Check for leaked passwords", "Clear report with priorities", "Re-scan after fixes"],
      fr: ["Scan de vulnérabilités du site et de PHP", "Analyse des ports ouverts et systèmes exposés", "Vérification des mots de passe divulgués", "Rapport clair avec priorités", "Nouveau scan après correction"],
    },
    faq: {
      nl: [
        { q: "Is een cyber scan schadelijk voor mijn systemen?", a: "Nee, we scannen veilig en in overleg, zonder je website of netwerk te verstoren." },
        { q: "Wat krijg ik na de scan?", a: "Een rapport in gewone taal met wat er mis is, hoe ernstig het is en wat je eraan kunt doen." },
        { q: "Hoe vaak moet ik scannen?", a: "Minimaal een keer per jaar, en na elke grote wijziging of melding van een nieuw lek." },
      ],
      en: [
        { q: "Can a cyber scan harm my systems?", a: "No, we scan safely and by agreement, without disrupting your website or network." },
        { q: "What do I get after the scan?", a: "A plain-language report of what's wrong, how serious it is and what to do about it." },
        { q: "How often should I scan?", a: "At least once a year, and after any major change or new vulnerability announcement." },
      ],
      fr: [
        { q: "Un scan peut-il endommager mes systèmes ?", a: "Non, nous scannons en toute sécurité et en concertation, sans perturber votre site ou réseau." },
        { q: "Que reçois-je après le scan ?", a: "Un rapport clair : ce qui ne va pas, la gravité et les actions à mener." },
        { q: "À quelle fréquence scanner ?", a: "Au moins une fois par an et après chaque changement majeur ou nouvelle faille." },
      ],
    },
    related: ["website-beveiliging", "cybersecurity", "firewall"],
  },
  {
    key: "firewall",
    icon: BrickWall,
    slug: { nl: "firewall-leveren", en: "firewall-supply", fr: "fourniture-pare-feu" },
    name: { nl: "Firewalls leveren & beheren", en: "Firewall supply & management", fr: "Fourniture & gestion de pare-feu" },
    placeTitle: { nl: "Firewall leveren en beheren in {place}", en: "Firewall supply and management in {place}", fr: "Fourniture et gestion de pare-feu à {place}" },
    short: {
      nl: "Zakelijke firewalls geleverd, geïnstalleerd en beheerd — als officieel Stormshield-distributeur.",
      en: "Business firewalls supplied, installed and managed — as official Stormshield distributor.",
      fr: "Pare-feu professionnels fournis, installés et gérés — distributeur officiel Stormshield.",
    },
    intro: {
      nl: "Een goede firewall is de voordeur van je bedrijfsnetwerk. Voor organisaties in {place} leveren en configureren we next-generation firewalls, zetten we veilige VPN-verbindingen op en houden we de firewall up-to-date.",
      en: "A good firewall is the front door of your business network. For organisations in {place} we supply and configure next-generation firewalls, set up secure VPN connections and keep the firewall up to date.",
      fr: "Un bon pare-feu est la porte d'entrée de votre réseau. Pour les organisations de {place}, nous fournissons et configurons des pare-feu nouvelle génération, mettons en place des VPN sécurisés et maintenons le pare-feu à jour.",
    },
    bullets: {
      nl: ["Next-generation firewalls (o.a. Stormshield)", "Veilig thuiswerken via VPN", "Inbraakdetectie en webfilter", "Beheer, updates en monitoring", "Advies over het juiste model"],
      en: ["Next-generation firewalls (incl. Stormshield)", "Secure remote working via VPN", "Intrusion prevention and web filtering", "Management, updates and monitoring", "Advice on the right model"],
      fr: ["Pare-feu nouvelle génération (dont Stormshield)", "Télétravail sécurisé par VPN", "Prévention d'intrusion et filtrage web", "Gestion, mises à jour et surveillance", "Conseil sur le bon modèle"],
    },
    faq: {
      nl: [
        { q: "Welke firewall past bij mijn bedrijf?", a: "Dat hangt af van het aantal gebruikers, de internetsnelheid en of je thuiswerkers hebt. Wij adviseren gratis." },
        { q: "Kunnen jullie mijn huidige firewall overnemen?", a: "Ja, we kunnen bestaande firewalls controleren, bijwerken en het beheer overnemen." },
        { q: "Is de router van mijn provider niet genoeg?", a: "Voor bedrijven meestal niet: een zakelijke firewall biedt veel betere bescherming en controle." },
      ],
      en: [
        { q: "Which firewall suits my business?", a: "It depends on users, internet speed and remote workers. Our advice is free." },
        { q: "Can you take over my current firewall?", a: "Yes, we can review, update and take over management of existing firewalls." },
        { q: "Isn't my ISP router enough?", a: "For businesses usually not: a business firewall offers far better protection and control." },
      ],
      fr: [
        { q: "Quel pare-feu pour mon entreprise ?", a: "Cela dépend du nombre d'utilisateurs, du débit et du télétravail. Notre conseil est gratuit." },
        { q: "Pouvez-vous reprendre mon pare-feu actuel ?", a: "Oui, nous pouvons l'auditer, le mettre à jour et en assurer la gestion." },
        { q: "Le routeur de mon opérateur ne suffit-il pas ?", a: "Pour une entreprise, généralement non : un pare-feu professionnel protège bien mieux." },
      ],
    },
    related: ["bedrijfsnetwerk", "security-hardware-software", "cybersecurity"],
  },
  {
    key: "bedrijfsnetwerk",
    icon: Network,
    slug: { nl: "bedrijfsnetwerk", en: "business-network", fr: "reseau-entreprise" },
    name: { nl: "Bedrijfsnetwerken", en: "Business networks", fr: "Réseaux d'entreprise" },
    placeTitle: { nl: "Bedrijfsnetwerk aanleggen en beheren in {place}", en: "Business network installation and management in {place}", fr: "Installation et gestion de réseau d'entreprise à {place}" },
    short: {
      nl: "Aanleg en beheer van veilige bedrijfsnetwerken: bekabeling, wifi, switches en VPN.",
      en: "Installation and management of secure business networks: cabling, Wi-Fi, switches and VPN.",
      fr: "Installation et gestion de réseaux sécurisés : câblage, Wi-Fi, switchs et VPN.",
    },
    intro: {
      nl: "Een stabiel en veilig netwerk is de basis van elk bedrijf in {place}. Wij ontwerpen, leggen aan en beheren je netwerk: van bekabeling en zakelijke wifi tot gescheiden gastnetwerken en veilige verbindingen tussen vestigingen.",
      en: "A stable and secure network is the foundation of every business in {place}. We design, install and manage your network: from cabling and business Wi-Fi to separate guest networks and secure links between sites.",
      fr: "Un réseau stable et sûr est la base de toute entreprise à {place}. Nous concevons, installons et gérons votre réseau : câblage, Wi-Fi professionnel, réseaux invités séparés et liaisons sécurisées entre sites.",
    },
    bullets: {
      nl: ["Netwerkontwerp en bekabeling", "Zakelijke wifi met dekking in het hele pand", "Managed switches en netwerkscheiding (VLAN)", "Veilige koppeling tussen vestigingen", "Monitoring en beheer op afstand"],
      en: ["Network design and cabling", "Business Wi-Fi with full coverage", "Managed switches and segmentation (VLAN)", "Secure links between sites", "Remote monitoring and management"],
      fr: ["Conception réseau et câblage", "Wi-Fi professionnel couvrant tout le bâtiment", "Switchs managés et segmentation (VLAN)", "Liaisons sécurisées entre sites", "Supervision et gestion à distance"],
    },
    faq: {
      nl: [
        { q: "Kunnen jullie ons bestaande netwerk verbeteren?", a: "Ja, we brengen je netwerk in kaart en lossen knelpunten in snelheid en veiligheid op." },
        { q: "Waarom een apart gastnetwerk?", a: "Zo kunnen bezoekers internetten zonder bij je bedrijfsgegevens te kunnen." },
        { q: "Doen jullie ook het beheer?", a: "Ja, we kunnen je netwerk doorlopend bewaken en onderhouden." },
      ],
      en: [
        { q: "Can you improve our existing network?", a: "Yes, we map your network and fix speed and security bottlenecks." },
        { q: "Why a separate guest network?", a: "Visitors can use the internet without access to your business data." },
        { q: "Do you handle management too?", a: "Yes, we can monitor and maintain your network continuously." },
      ],
      fr: [
        { q: "Pouvez-vous améliorer notre réseau existant ?", a: "Oui, nous l'analysons et corrigeons les problèmes de débit et de sécurité." },
        { q: "Pourquoi un réseau invité séparé ?", a: "Les visiteurs accèdent à Internet sans toucher à vos données." },
        { q: "Assurez-vous la gestion ?", a: "Oui, nous pouvons surveiller et maintenir votre réseau en continu." },
      ],
    },
    related: ["firewall", "security-hardware-software", "cybersecurity"],
  },
];

export const SERVICE_BY_KEY: Record<string, ServiceDef> = Object.fromEntries(SERVICES.map((s) => [s.key, s]));

export const langPrefix = (lang: Lang) => (lang === "nl" ? "" : `/${lang}`);

export function servicePath(key: string, lang: Lang, ...segments: (string | undefined)[]) {
  const s = SERVICE_BY_KEY[key];
  const rest = segments.filter(Boolean).join("/");
  return `${langPrefix(lang)}/${s.slug[lang]}${rest ? `/${rest}` : ""}`;
}

export const STATIC_PATHS: Record<string, L<string>> = {
  home: { nl: "/", en: "/en", fr: "/fr" },
  about: { nl: "/over-ons", en: "/en/about", fr: "/fr/a-propos" },
  contact: { nl: "/contact", en: "/en/contact", fr: "/fr/contact" },
  privacy: { nl: "/privacy", en: "/privacy-policy", fr: "/politique-confidentialite" },
  terms: { nl: "/voorwaarden", en: "/terms", fr: "/conditions-generales" },
  cookies: { nl: "/cookiebeleid", en: "/cookie-policy", fr: "/cookiebeleid" },
};

export function langFromPath(pathname: string): Lang {
  if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
  if (pathname === "/fr" || pathname.startsWith("/fr/")) return "fr";
  if (["/privacy-policy", "/terms", "/cookie-policy", "/cookie-preferences"].includes(pathname)) return "en";
  if (["/politique-confidentialite", "/conditions-generales"].includes(pathname)) return "fr";
  return "nl";
}

/** Translate the current path to the equivalent page in another language. */
export function translatePath(pathname: string, to: Lang): string {
  const from = langFromPath(pathname);
  for (const paths of Object.values(STATIC_PATHS)) {
    if (paths[from] === pathname) return paths[to];
  }
  const stripped = from === "nl" ? pathname : pathname.slice(3);
  const [first, ...rest] = stripped.split("/").filter(Boolean);
  const svc = SERVICES.find((s) => s.slug[from] === first);
  if (svc) return servicePath(svc.key, to, ...rest);
  return STATIC_PATHS.home[to];
}

export const fill = (text: string, place: string) => text.split("{place}").join(place);
